import React, { useCallback, useEffect, useState } from 'react'
import { Helmet } from 'react-helmet'
import { useNavigate } from 'react-router-dom'
import { IoIosArrowBack } from 'react-icons/io'
import '../styles/admin.css'
import { mediaPath } from '../utils/mediaRoute'

const DEVICE_KEY = 'pinweelo-admin-device-v1'
const TABS = ['overview', 'users', 'posts', 'reports', 'payments', 'careers']

const deviceToken = () => {
  let token = localStorage.getItem(DEVICE_KEY)
  if (token && token.length >= 32) return token
  const bytes = new Uint8Array(32)
  window.crypto.getRandomValues(bytes)
  token = Array.from(bytes, (b) => b.toString(16).padStart(2, '0')).join('')
  localStorage.setItem(DEVICE_KEY, token)
  return token
}

const authBits = () => {
  try {
    return JSON.parse(localStorage.getItem('userdata') || '{}')
  } catch (err) {
    return {}
  }
}

const adminPost = async (path, body = {}) => {
  const { id, pid } = authBits()
  const res = await fetch(`/api/admin/${path}`, {
    method: 'POST',
    headers: {
      'content-type': 'application/json',
      'x-admin-user': id || '',
      'x-admin-pid': pid || '',
      'x-admin-device': deviceToken()
    },
    body: JSON.stringify({ userid: id, pid, ...body })
  })
  const data = await res.json().catch(() => ({}))
  if (!res.ok) throw new Error(data.msg || 'forbidden')
  return data
}

const when = (value) => {
  const n = Number(value)
  if (!n) return value ? String(value) : '—'
  if (n < 0) return 'forever'
  const d = new Date(n)
  if (Number.isNaN(d.getTime())) return String(value)
  return d.toLocaleString()
}

const Admin = () => {
  const navigate = useNavigate()
  const [gate, setGate] = useState('check')
  const [error, setError] = useState('')
  const [bindCode, setBindCode] = useState('')
  const [tab, setTab] = useState('overview')
  const [stats, setStats] = useState(null)
  const [users, setUsers] = useState([])
  const [posts, setPosts] = useState([])
  const [reports, setReports] = useState([])
  const [payments, setPayments] = useState([])
  const [careers, setCareers] = useState([])
  const [q, setQ] = useState('')
  const [busy, setBusy] = useState('')
  const [replyFor, setReplyFor] = useState('')
  const [replyText, setReplyText] = useState('')
  const [reasons, setReasons] = useState({})

  const openPost = (item) => {
    const post = {
      id: item && (item.id || item.postid),
      type: item && (item.type || item.postType),
      image: item && item.image
    }
    if (!post.id) {
      setError('That post is gone')
      return
    }
    navigate(mediaPath(post))
  }

  const boot = useCallback(async () => {
    if (!authBits().id) {
      navigate('/auth', { replace: true, state: { from: '/admin' } })
      return
    }
    try {
      await adminPost('session')
      setGate('ok')
      setError('')
    } catch (err) {
      setGate('enroll')
      setError(err.message === 'forbidden' || err.message === 'device'
        ? 'This Mac is not bound yet. Enter the bind code from the server .env file.'
        : err.message)
    }
  }, [navigate])

  useEffect(() => { boot() }, [boot])

  const loadTab = useCallback(async (name, query) => {
    setBusy(name)
    try {
      if (name === 'overview') setStats((await adminPost('overview')).stats)
      if (name === 'users') setUsers((await adminPost('users', { q: query })).users || [])
      if (name === 'posts') setPosts((await adminPost('posts', { q: query })).posts || [])
      if (name === 'reports') setReports((await adminPost('reports')).reports || [])
      if (name === 'payments') setPayments((await adminPost('payments')).payments || [])
      if (name === 'careers') setCareers((await adminPost('careers')).careers || [])
      setError('')
    } catch (err) {
      setError(err.message)
      if (err.message === 'forbidden' || err.message === 'device') setGate('enroll')
    } finally {
      setBusy('')
    }
  }, [])

  useEffect(() => {
    if (gate !== 'ok') return undefined
    loadTab(tab, q)
    return undefined
  }, [gate, tab, loadTab])

  const enroll = async (event) => {
    event.preventDefault()
    setBusy('enroll')
    try {
      await adminPost('enroll', { bindCode, deviceToken: deviceToken() })
      setGate('ok')
      setError('')
    } catch (err) {
      setError(err.message === 'bad bind code'
        ? 'Wrong bind code. Copy ADMIN_BIND_CODE from ickserverend/.env'
        : err.message === 'not billy'
          ? 'Sign in as Billy Wilk first, then bind this Mac.'
          : err.message || 'Could not bind this Mac')
    } finally {
      setBusy('')
    }
  }

  const act = async (path, body, thenTab) => {
    setBusy(path)
    try {
      await adminPost(path, body)
      await loadTab(thenTab || tab, q)
      return true
    } catch (err) {
      setError(err.message)
      return false
    } finally {
      setBusy('')
    }
  }

  return (
    <section className="AdminPage">
      <Helmet>
        <title>Pinweelo control</title>
        <meta name="robots" content="noindex,nofollow" />
        <meta name="theme-color" content="#000000" />
      </Helmet>
      <header className="adminTop">
        <button type="button" className="adminBack" onClick={() => navigate('/profile')} aria-label="Back">
          <IoIosArrowBack />
        </button>
        <h1>Control</h1>
        <span />
      </header>

      {gate === 'check' ? <p className="adminNote">Checking this Mac…</p> : null}

      {gate === 'enroll' ? (
        <form className="adminEnroll" onSubmit={enroll}>
          <h2>Bind this MacBook</h2>
          <p>Only Billy Wilk can open this, and only after this machine is enrolled with the bind code on the server.</p>
          <input
            value={bindCode}
            onChange={(event) => setBindCode(event.target.value)}
            placeholder="Bind code"
            autoComplete="off"
          />
          {error ? <p className="adminErr">{error}</p> : null}
          <button type="submit" disabled={busy === 'enroll'}>Enroll this Mac</button>
        </form>
      ) : null}

      {gate === 'ok' ? (
        <div className="adminShell">
          <nav className="adminTabs">
            {TABS.map((name) => (
              <button key={name} type="button" className={tab === name ? 'isOn' : ''} onClick={() => setTab(name)}>
                {name}
              </button>
            ))}
          </nav>
          {error ? <p className="adminErr">{error}</p> : null}
          {(tab === 'users' || tab === 'posts') ? (
            <form className="adminSearch" onSubmit={(event) => { event.preventDefault(); loadTab(tab, q) }}>
              <input value={q} onChange={(event) => setQ(event.target.value)} placeholder={`Search ${tab}`} />
              <button type="submit">Search</button>
            </form>
          ) : null}

          {tab === 'overview' && stats ? (
            <div className="adminStats">
              <article><b>{stats.users}</b><span>Users</span></article>
              <article><b>{stats.visits}</b><span>Video visits</span></article>
              <article><b>{stats.posts}</b><span>Posts</span></article>
              <article><b>{stats.reports}</b><span>Reports</span></article>
              <article><b>{stats.payments}</b><span>Payments</span></article>
              <article><b>{stats.careers}</b><span>Careers</span></article>
              <article><b>{stats.blocks}</b><span>Blocks</span></article>
            </div>
          ) : null}

          {tab === 'users' ? (
            <div className="adminTableWrap">
              <table className="adminTable">
                <thead>
                  <tr>
                    <th>User</th>
                    <th>IP / country</th>
                    <th>Kind</th>
                    <th>Posts</th>
                    <th>Block</th>
                    <th>Sub</th>
                  </tr>
                </thead>
                <tbody>
                  {users.map((user) => (
                    <tr key={user.id}>
                      <td>
                        <strong>@{user.id}</strong>
                        <small>{user.name || user.email}</small>
                        <small>{user.lastSeen ? when(user.lastSeen) : 'never seen'}</small>
                      </td>
                      <td>
                        <span>{user.ip || '—'}</span>
                        <small>{user.country || 'unknown'}</small>
                      </td>
                      <td><em className={`kind kind-${user.kind}`}>{user.kind}</em></td>
                      <td>{user.posts}</td>
                      <td>
                        {user.blocked ? <small>until {when(user.blockedUntil)}{user.blockReason ? ` · ${user.blockReason}` : ''}</small> : null}
                        <input
                          className="adminReason"
                          value={reasons[user.id] || ''}
                          onChange={(event) => setReasons((prev) => ({ ...prev, [user.id]: event.target.value }))}
                          placeholder="Reason"
                        />
                        <select defaultValue="" onChange={(event) => {
                          const hours = Number(event.target.value)
                          if (!event.target.value) return
                          if (hours !== 0 && !(reasons[user.id] || '').trim()) {
                            setError('Type a reason before blocking')
                            event.target.value = ''
                            return
                          }
                          act('block-user', { target: user.id, hours, reason: (reasons[user.id] || '').trim() }, 'users')
                          event.target.value = ''
                        }}>
                          <option value="">block…</option>
                          <option value="1">1 hour</option>
                          <option value="24">1 day</option>
                          <option value="168">7 days</option>
                          <option value="720">30 days</option>
                          <option value="-1">forever</option>
                          <option value="0">unblock</option>
                        </select>
                      </td>
                      <td>
                        <select defaultValue="" onChange={(event) => {
                          const days = Number(event.target.value)
                          if (!event.target.value) return
                          act('grant-sub', { target: user.id, days, plan: 'admin-free' }, 'payments')
                          event.target.value = ''
                        }}>
                          <option value="">free sub…</option>
                          <option value="7">7 days</option>
                          <option value="30">30 days</option>
                          <option value="365">1 year</option>
                          <option value="-1">forever</option>
                        </select>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          ) : null}

          {tab === 'posts' ? (
            <div className="adminTableWrap">
              <table className="adminTable">
                <thead>
                  <tr>
                    <th>Post</th>
                    <th>Owner</th>
                    <th>Views</th>
                    <th></th>
                  </tr>
                </thead>
                <tbody>
                  {posts.map((post) => (
                    <tr key={post.id}>
                      <td>
                        <button type="button" className="adminLink" onClick={() => openPost(post)}>
                          <strong>{post.title}</strong>
                          <small>{post.type} · {post.id}</small>
                        </button>
                      </td>
                      <td>{post.email || post.userid}</td>
                      <td>{post.views}</td>
                      <td>
                        <button type="button" className="adminDanger" onClick={() => {
                          if (window.confirm(`Delete ${post.title}?`)) act('delete-post', { postid: post.id }, 'posts')
                        }}>Delete</button>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          ) : null}

          {tab === 'reports' ? (
            <div className="adminTableWrap">
              <table className="adminTable">
                <thead>
                  <tr>
                    <th>Reporter</th>
                    <th>Video</th>
                    <th>Why</th>
                    <th>Where</th>
                    <th></th>
                  </tr>
                </thead>
                <tbody>
                  {reports.map((row) => (
                    <tr key={row.id}>
                      <td>
                        <strong>@{row.userid}</strong>
                        <small>{row.reporter || ''}</small>
                      </td>
                      <td>
                        <button type="button" className="adminLink" onClick={() => openPost(row)}>
                          <strong>{row.video || row.postid}</strong>
                          <small>owner @{row.owner || '—'}</small>
                        </button>
                      </td>
                      <td>{row.content}</td>
                      <td>{row.country || '—'}</td>
                      <td>
                        <div className="adminRowActs">
                          <button type="button" onClick={() => { setReplyFor(row.id); setReplyText('') }}>Reply</button>
                          <button type="button" className="adminDanger" onClick={() => {
                            if (window.confirm('Remove this report?')) act('delete-report', { reportid: row.id }, 'reports')
                          }}>Remove</button>
                        </div>
                        {replyFor === row.id ? (
                          <form className="adminReply" onSubmit={async (event) => {
                            event.preventDefault()
                            if (!replyText.trim()) return
                            const ok = await act('reply-report', { reportid: row.id, message: replyText.trim() }, 'reports')
                            if (ok) {
                              setReplyFor('')
                              setReplyText('')
                            }
                          }}>
                            <textarea
                              value={replyText}
                              onChange={(event) => setReplyText(event.target.value)}
                              placeholder="Reply to this user…"
                              rows={3}
                            />
                            <button type="submit">Send to Alerts</button>
                          </form>
                        ) : null}
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          ) : null}

          {tab === 'payments' ? (
            <div className="adminTableWrap">
              <table className="adminTable">
                <thead>
                  <tr>
                    <th>User</th>
                    <th>Plan</th>
                    <th>Price</th>
                    <th>Until</th>
                  </tr>
                </thead>
                <tbody>
                  {payments.map((row) => (
                    <tr key={row.id}>
                      <td>
                        <strong>@{row.userid}</strong>
                        <small>{row.first_name || row.email}</small>
                      </td>
                      <td>{row.plan}</td>
                      <td>{row.price}</td>
                      <td>{when(row.until_ms) || row.date}</td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          ) : null}

          {tab === 'careers' ? (
            <div className="adminTableWrap">
              <table className="adminTable">
                <thead>
                  <tr>
                    <th>Person</th>
                    <th>Role</th>
                    <th>Note</th>
                    <th>When</th>
                    <th></th>
                  </tr>
                </thead>
                <tbody>
                  {careers.map((row) => (
                    <tr key={row.id}>
                      <td>
                        <strong>{row.name}</strong>
                        <small>{row.email}</small>
                        <small>{row.phone || 'no phone'}</small>
                        <small>{row.location || '—'}</small>
                        {row.portfolio ? (
                          <small>
                            <a href={row.portfolio} target="_blank" rel="noopener noreferrer">{row.portfolio}</a>
                          </small>
                        ) : null}
                      </td>
                      <td>{row.role}</td>
                      <td className="adminCareerNote">{row.message}</td>
                      <td>{when(row.created_at)}</td>
                      <td>
                        <button type="button" className="adminDanger" onClick={() => {
                          if (window.confirm(`Remove ${row.name}?`)) act('delete-career', { id: row.id }, 'careers')
                        }}>Remove</button>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
              {!careers.length && !busy ? <p className="adminNote">No applications yet.</p> : null}
            </div>
          ) : null}
          {busy ? <p className="adminNote">Working…</p> : null}
        </div>
      ) : null}
    </section>
  )
}

export default Admin
